import { useCallback } from 'react';
import { useUserControlsContext, useUserControlsLoaded } from '../contexts/UserControlsContext';
import { useLayoutPrefs } from './useLayoutPrefs';
import type { FlowRole } from '@shared/flow/role';
import { applyLens } from '@shared/flow/role-view';

export interface RoleView {
  /** EFFECTIVE team (the lens, when one is set; otherwise the real team). */
  team: FlowRole | null;
  /** The rep's real team, unaffected by any lens — for the menu's "which team am I really" copy. */
  realTeam: FlowRole | null;
  /** True while a lens is active (effective team differs from the real one). */
  viewingAs: boolean;
  /** True once /api/me and the view preferences have both loaded. */
  isLoaded: boolean;
}

/**
 * Read-only view of the role view switcher's resolved state. Derived from the
 * real team in `UserControlsContext` and the stored lens in `useLayoutPrefs`'s
 * cache, through the same `applyLens` the server's `/api/me` uses. No state of
 * its own. While the preferences are unavailable (a failed first GET), it
 * reads the effective team the server already applied in `/api/me`.
 */
export function useRoleView(): RoleView {
  const controls = useUserControlsContext();
  const meLoaded = useUserControlsLoaded();
  const { viewAsRole, prefsLoaded, prefsAvailable } = useLayoutPrefs();
  // Prefs unavailable (the first GET failed): the server's own effective
  // fields in /api/me carry the stored lens, so they stand in for the cache.
  // DEFAULTS' `viewAsRole: null` would read a saved lens as "no lens".
  const lens = prefsAvailable
    ? applyLens({ realUserType: controls.realUserType, realFlowRole: controls.realFlowRole, viewAsRole })
    : { flowRole: controls.flowRole, viewingAs: controls.viewingAs };
  return { team: lens.flowRole, realTeam: controls.realFlowRole, viewingAs: lens.viewingAs, isLoaded: meLoaded && prefsLoaded };
}

/**
 * Write side of the role view switcher's team lens. Writes through
 * `useLayoutPrefs`'s one mutation (`setViewAsRole`), never a second
 * `useMutation` or a second query on `PREFS_KEY`. `useLayoutPrefs` updates its prefs cache when the write starts,
 * which `useRoleView()` reads, so the pick shows at once; it invalidates
 * `['/api/me']` on settle so role-gated UI follows the server's effective team.
 *
 * No `saveStatus`: the rail account menu has no separate failure indicator. On a failed write the check and the
 * pill move back to the previous lens, and the settle refetch re-confirms
 * the server's stored lens.
 */
export function useSetViewAsRole(): { setTeam: (team: FlowRole | null) => void } {
  const { setViewAsRole } = useLayoutPrefs();
  const setTeam = useCallback((team: FlowRole | null) => setViewAsRole(team), [setViewAsRole]);
  return { setTeam };
}
